import React, { useState, useEffect, useRef } from "react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Shield, FileText, BarChart3, Users, Zap, Globe, Clock, DollarSign,
  ArrowRight, ChevronRight, Lock, Mail, Eye, EyeOff, Loader2, CheckCircle,
  Activity, AlertTriangle, Target, TrendingUp, Layers, Radio,
  Scale, Cpu, Briefcase, Building2, Play, GitBranch, Gavel, CalendarClock,
  FileSearch
} from "lucide-react";
import FeatureShowcase from "../components/FeatureShowcase";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HERO_IMG = "https://images.unsplash.com/photo-1589994965851-a8f479c573a9?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600";

const CAPABILITIES = [
  { icon: Clock, title: "Forensic Delay Analysis", metric: "42d", metricLabel: "Avg EoT Saved", desc: "Window, APC, collapsed as-built" },
  { icon: Layers, title: "Programme Baseline Compare", metric: "P6+MSP", metricLabel: "Native", desc: "Activity-level drift + narrative" },
  { icon: Scale, title: "Claims Pipeline", metric: "87%", metricLabel: "Prevented", desc: "Notice triggers &amp; quantum" },
  { icon: GitBranch, title: "Change & Variation", metric: "Live", metricLabel: "Register", desc: "EoT &amp; cost impact tracker" },
  { icon: Shield, title: "Risk Heatmap", metric: "94%", metricLabel: "Accuracy", desc: "AI pinch-point detection" },
  { icon: Radio, title: "Evidence Room", metric: "24/7", metricLabel: "Live Collab", desc: "Multi-user forensic workspace" },
];

// Scanning line animation CSS
const scanLineCSS = `
@keyframes scanLine {
  0% { top: 0%; opacity: 1; }
  100% { top: 100%; opacity: 0; }
}
@keyframes pulse-ring {
  0% { transform: scale(0.8); opacity: 1; }
  100% { transform: scale(1.4); opacity: 0; }
}
@keyframes slideUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes statusPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
.scan-line {
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent 0%, #EAB308 50%, transparent 100%);
  animation: scanLine 4s linear infinite;
}
.anim-slide-up { animation: slideUp 0.6s ease-out forwards; opacity: 0; }
.anim-fade-in { animation: fadeIn 0.8s ease-out forwards; opacity: 0; }
.status-pulse { animation: statusPulse 2s ease-in-out infinite; }
`;

const SignInPage = ({ onAuth }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showShowcase, setShowShowcase] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [otp, setOtp] = useState("");
  const [sessionToken, setSessionToken] = useState("");
  const [otpEmail, setOtpEmail] = useState("");
  const [formData, setFormData] = useState({ email: "", password: "", name: "", company: "" });
  const [activeCapIdx, setActiveCapIdx] = useState(0);
  const [systemTime, setSystemTime] = useState(new Date());

  // Rotate capability highlight
  useEffect(() => {
    const t = setInterval(() => setActiveCapIdx(p => (p + 1) % CAPABILITIES.length), 3000);
    return () => clearInterval(t);
  }, []);
  
  // System clock
  useEffect(() => {
    const t = setInterval(() => setSystemTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const res = await fetch(`${BACKEND_URL}/api/auth/login`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: formData.email, password: formData.password })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Login failed");
        if (data.otp_required) {
          setSessionToken(data.session_token);
          setOtpEmail(data.email);
          setOtpStep(true);
          toast.success("OTP sent to your email!");
        }
      } else {
        const res = await fetch(`${BACKEND_URL}/api/auth/register`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData)
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Registration failed");
        if (data.otp_required) {
          setSessionToken(data.session_token);
          setOtpEmail(data.email || formData.email);
          setOtpStep(true);
          toast.success("Account created! Verify OTP to continue.");
        } else if (data.access_token) {
          onAuth(data.access_token, data.user);
          toast.success("Welcome! 7-day trial started.");
        }
      }
    } catch (error) {
      toast.error(error.message || "Authentication failed");
    } finally { setLoading(false); }
  };

  const handleOTPVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/verify-otp`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: otpEmail, otp, session_token: sessionToken })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Invalid OTP");
      onAuth(data.access_token, data.user);
      toast.success("Welcome to GPI.Forensic!");
    } catch (error) {
      toast.error(error.message || "Invalid OTP");
    } finally { setLoading(false); }
  };

  const handleResendOTP = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/resend-otp`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: otpEmail, session_token: sessionToken })
      });
      toast.success("New OTP sent!");
    } catch (e) { toast.error("Failed to resend"); }
  };

  return (
    <div className="min-h-screen bg-[#050508] flex" data-testid="sign-in-page">
      <style>{scanLineCSS}</style>
      
      {/* Feature Showcase Modal */}
      {showShowcase && <FeatureShowcase onClose={() => setShowShowcase(false)} />}

      {/* LEFT PANEL - Command Center Visual */}
      <div className="hidden lg:flex lg:w-[58%] relative overflow-hidden flex-col">
        
        {/* Background image with overlay */}
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050508] via-[#050508]/80 to-[#050508]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050508] via-transparent to-[#050508]/60" />
        </div>

        {/* Scan line effect */}
        <div className="scan-line" />
        
        {/* Grid overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `linear-gradient(rgba(0,71,171,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(0,71,171,0.3) 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }} />

        {/* Content */}
        <div className="relative z-10 flex flex-col h-full p-10">
          
          {/* Top bar */}
          <div className="flex items-center justify-between mb-auto">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#E11D48] flex items-center justify-center" style={{ clipPath: 'polygon(0 0, 100% 0, 100% 70%, 70% 100%, 0 100%)' }}>
                <span className="text-white font-black text-lg font-['Chivo']">G</span>
              </div>
              <div>
                <h1 className="text-lg font-black font-['Chivo'] text-white uppercase tracking-wider leading-none">
                  GPI<span className="text-[#E11D48]">.</span>FORENSICS
                </h1>
                <p className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 font-mono">Delay Analysis <span className="text-[#E11D48]">|</span> Claims Control</p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-[10px] font-mono text-zinc-500 uppercase tracking-wider">
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 status-pulse" />
                <span>System Online</span>
              </div>
              <span className="text-zinc-700">|</span>
              <span>{systemTime.toLocaleTimeString('en-GB', { hour12: false })} UTC</span>
            </div>
          </div>

          {/* Main headline */}
          <div className="flex-1 flex flex-col justify-center max-w-xl">
            <div className="anim-slide-up" style={{ animationDelay: '0.1s' }}>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-px bg-[#E11D48]" />
                <span className="text-[10px] uppercase tracking-[0.4em] text-[#E11D48] font-mono font-bold">Forensic Planning Platform</span>
              </div>
              <h2 className="text-5xl font-black font-['Chivo'] text-white leading-[1.05] tracking-tight">
                FORENSIC<br />
                DELAY<br />
                <span className="text-[#E11D48]">CONTROL</span><span className="text-[#EAB308]">.</span>
              </h2>
              <p className="text-zinc-400 text-sm mt-5 max-w-sm leading-relaxed font-light">
                Baseline-vs-baseline programme forensics, EoT &amp; disruption claim tracking, and
                contract-driven evidence control — purpose-built for planners &amp; claims consultants.
              </p>
              
              {/* Watch Demo Button */}
              <button 
                onClick={() => setShowShowcase(true)} 
                className="mt-6 flex items-center gap-3 group cursor-pointer"
                data-testid="btn-watch-demo"
              >
                <div className="w-11 h-11 border-2 border-[#E11D48] flex items-center justify-center relative group-hover:bg-[#E11D48]/10 transition-all duration-300">
                  <Play size={16} className="text-[#E11D48] ml-0.5" />
                  <div className="absolute inset-0 border border-[#E11D48]/30 scale-125 opacity-0 group-hover:opacity-100 group-hover:scale-150 transition-all duration-500" />
                </div>
                <div>
                  <p className="text-sm font-bold text-white group-hover:text-[#E11D48] transition-colors font-['Chivo'] uppercase tracking-wide">Watch Platform Demo</p>
                  <p className="text-[10px] text-zinc-600 font-mono">Interactive forensic workflow tour</p>
                </div>
              </button>
            </div>

            {/* Capability cards - command center style */}
            <div className="mt-10 anim-slide-up" style={{ animationDelay: '0.3s' }}>
              <div className="grid grid-cols-3 gap-2">
                {CAPABILITIES.map((cap, i) => {
                  const isActive = i === activeCapIdx;
                  return (
                    <div
                      key={i}
                      className={`relative p-3 border transition-all duration-500 cursor-default group ${
                        isActive 
                          ? 'border-[#EAB308]/60 bg-[#EAB308]/10 backdrop-blur-sm' 
                          : 'border-zinc-800/60 bg-zinc-900/30 backdrop-blur-sm hover:border-zinc-700'
                      }`}
                      data-testid={`tool-card-${i}`}
                    >
                      {isActive && (
                        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-[#EAB308] to-transparent" />
                      )}
                      <div className="flex items-center justify-between mb-2">
                        <cap.icon size={14} className={`${isActive ? 'text-[#EAB308]' : 'text-zinc-600'} transition-colors`} />
                        <div className={`text-right ${isActive ? 'text-white' : 'text-zinc-500'}`}>
                          <span className="text-lg font-black font-['Chivo'] leading-none block">{cap.metric}</span>
                          <span className="text-[8px] uppercase tracking-wider font-mono">{cap.metricLabel}</span>
                        </div>
                      </div>
                      <p className={`text-[11px] font-bold leading-tight ${isActive ? 'text-white' : 'text-zinc-400'}`}>{cap.title}</p>
                      <p className="text-[9px] text-zinc-600 mt-0.5 font-mono">{cap.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Bottom status bar */}
          <div className="anim-fade-in mt-auto" style={{ animationDelay: '0.5s' }}>
            <div className="border-t border-zinc-800/60 pt-4 flex items-center justify-between">
              <div className="flex items-center gap-6">
                {[
                  { v: "P6+MSP", l: "NATIVE IMPORT" },
                  { v: "42d", l: "AVG EoT" },
                  { v: "87%", l: "CLAIMS WON" },
                  { v: "99.9%", l: "UPTIME" },
                ].map((stat, i) => (
                  <div key={i} className="text-center">
                    <p className="text-sm font-black text-white font-['Chivo']">{stat.v}</p>
                    <p className="text-[8px] uppercase tracking-[0.2em] text-zinc-600 font-mono">{stat.l}</p>
                  </div>
                ))}
              </div>
              <div className="text-[9px] text-zinc-700 font-mono uppercase tracking-wider">
                v3.2.0 | Secure Connection
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT PANEL - Auth Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 relative bg-[#050508]">
        
        {/* Subtle grid on form side */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)`,
          backgroundSize: '24px 24px'
        }} />

        <div className="w-full max-w-md relative z-10">
          
          {/* Mobile header */}
          <div className="lg:hidden mb-10 text-center">
            <div className="flex items-center justify-center gap-2 mb-3">
              <div className="w-9 h-9 bg-[#E11D48] flex items-center justify-center" style={{ clipPath: 'polygon(0 0, 100% 0, 100% 70%, 70% 100%, 0 100%)' }}>
                <span className="text-white font-black font-['Chivo']">G</span>
              </div>
              <h1 className="text-lg font-black font-['Chivo'] text-white uppercase tracking-wider">
                GPI<span className="text-[#E11D48]">.</span>FORENSICS
              </h1>
            </div>
            <p className="text-zinc-600 text-xs font-mono uppercase tracking-widest">Delay Analysis <span className="text-[#E11D48]">|</span> Claims Control</p>
          </div>

          {otpStep ? (
            /* OTP Verification */
            <div className="anim-slide-up" style={{ animationDelay: '0.1s' }}>
              <div className="text-center mb-10">
                <div className="w-16 h-16 border-2 border-[#EAB308] mx-auto mb-5 flex items-center justify-center relative">
                  <Lock size={24} className="text-[#EAB308]" />
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-[#E11D48]" />
                </div>
                <h2 className="text-2xl font-black text-white font-['Chivo'] uppercase tracking-wide">Identity Verification</h2>
                <p className="text-zinc-500 text-xs mt-3 font-mono uppercase tracking-wider">Secure OTP Sent To</p>
                <p className="text-[#EAB308] font-bold text-sm mt-1">{otpEmail}</p>
              </div>

              <form onSubmit={handleOTPVerify} className="space-y-6">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.3em] text-zinc-600 mb-3 font-mono font-bold">Verification Code</label>
                  <input type="text" value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full bg-transparent border-2 border-zinc-800 text-white text-center text-3xl tracking-[0.5em] font-mono p-4 focus:border-[#EAB308] outline-none transition-colors"
                    placeholder="------" maxLength={6} required autoFocus data-testid="otp-input" />
                </div>
                <Button type="submit" disabled={loading || otp.length !== 6}
                  className="w-full bg-[#EAB308] hover:bg-[#CA8A04] text-white py-4 h-auto text-sm font-bold uppercase tracking-widest transition-all" data-testid="btn-verify">
                  {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <CheckCircle size={16} className="mr-2" />}
                  Verify & Access System
                </Button>
                <div className="flex justify-between text-xs">
                  <button type="button" onClick={handleResendOTP} className="text-[#EAB308] hover:text-[#FDE047] font-mono uppercase tracking-wider transition-colors">Resend Code</button>
                  <button type="button" onClick={() => setOtpStep(false)} className="text-zinc-600 hover:text-white font-mono uppercase tracking-wider transition-colors">Back</button>
                </div>
              </form>
            </div>
          ) : (
            /* Login / Register Form */
            <div>
              <div className="mb-8 anim-slide-up" style={{ animationDelay: '0.1s' }}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-px bg-[#E11D48]" />
                  <span className="text-[9px] uppercase tracking-[0.4em] text-[#E11D48] font-mono font-bold">
                    {isLogin ? "Secure Access" : "New Account"}
                  </span>
                </div>
                <h2 className="text-3xl font-black text-white font-['Chivo'] uppercase tracking-tight">
                  {isLogin ? "Sign In" : "Register"}
                </h2>
                <p className="text-zinc-500 mt-2 text-sm">
                  {isLogin ? "Access your forensic planning workspace" : "Start defending schedules &amp; claims today"}
                </p>
              </div>

              {!isLogin && (
                <div className="mb-6 space-y-3 anim-fade-in" style={{ animationDelay: '0.15s' }}>
                  {/* Trial banner */}
                  <div className="border border-emerald-900/60 bg-emerald-950/20 p-4 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-emerald-500 via-emerald-400 to-transparent" />
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-9 h-9 border border-emerald-700 flex items-center justify-center flex-shrink-0 bg-emerald-950/60">
                        <Zap size={16} className="text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-emerald-300 text-xs font-bold font-['Chivo'] uppercase tracking-wide">7-Day Enterprise Trial</p>
                        <p className="text-emerald-600 text-[10px] font-mono">Full platform access. No credit card required.</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {[
                        { icon: Clock, text: "Delay Analysis" },
                        { icon: Scale, text: "Claims Register" },
                        { icon: Layers, text: "Baseline Compare" },
                      ].map((item, i) => (
                        <div key={i} className="flex items-center gap-1.5 bg-emerald-950/40 border border-emerald-900/40 px-2 py-1.5">
                          <item.icon size={10} className="text-emerald-500 flex-shrink-0" />
                          <span className="text-[8px] text-emerald-500/90 font-mono font-bold uppercase tracking-wide">{item.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* What you get */}
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { icon: Clock, label: "Delay Analysis", sub: "Window & APC methods" },
                      { icon: Layers, label: "P6 / MSP Import", sub: "Baseline-vs-baseline compare" },
                      { icon: Scale, label: "Claim Prevention", sub: "87% notice hit-rate" },
                      { icon: GitBranch, label: "Change Register", sub: "EoT & cost tracking" },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-2 p-2 border border-zinc-800/50 bg-zinc-900/20 hover:border-[#EAB308]/30 transition-all">
                        <div className="w-6 h-6 flex items-center justify-center border border-zinc-800 bg-zinc-900/60 flex-shrink-0 mt-0.5">
                          <item.icon size={11} className="text-[#EAB308]" />
                        </div>
                        <div>
                          <p className="text-[10px] font-bold text-zinc-300">{item.label}</p>
                          <p className="text-[8px] text-zinc-600 font-mono">{item.sub}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5 anim-slide-up" style={{ animationDelay: '0.2s' }}>
                {!isLogin && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.3em] text-zinc-600 mb-2 font-mono font-bold">Full Name</label>
                      <input type="text" value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                        className="w-full bg-zinc-900/40 border border-zinc-800 text-white px-3 py-3 focus:border-[#EAB308] focus:bg-zinc-900/60 outline-none placeholder-zinc-700 transition-all text-sm"
                        placeholder="John Smith" required data-testid="signin-name" />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.3em] text-zinc-600 mb-2 font-mono font-bold">Company</label>
                      <input type="text" value={formData.company} onChange={e => setFormData(p => ({ ...p, company: e.target.value }))}
                        className="w-full bg-zinc-900/40 border border-zinc-800 text-white px-3 py-3 focus:border-[#EAB308] focus:bg-zinc-900/60 outline-none placeholder-zinc-700 transition-all text-sm"
                        placeholder="Acme Construction" data-testid="signin-company" />
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.3em] text-zinc-600 mb-2 font-mono font-bold">Email Address</label>
                  <div className="relative">
                    <input type="email" value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                      className={`w-full text-white outline-none placeholder-zinc-700 transition-all text-sm ${
                        isLogin 
                          ? 'bg-transparent border-b-2 border-zinc-800 px-0 py-3 focus:border-[#EAB308]' 
                          : 'bg-zinc-900/40 border border-zinc-800 px-3 py-3 focus:border-[#EAB308] focus:bg-zinc-900/60'
                      }`}
                      placeholder="you@company.com" required data-testid="signin-email" />
                    <Mail size={14} className={`absolute top-1/2 -translate-y-1/2 text-zinc-700 ${isLogin ? 'right-0' : 'right-3'}`} />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.3em] text-zinc-600 mb-2 font-mono font-bold">Password</label>
                  <div className="relative">
                    <input type={showPassword ? "text" : "password"} value={formData.password}
                      onChange={e => setFormData(p => ({ ...p, password: e.target.value }))}
                      className={`w-full text-white outline-none placeholder-zinc-700 transition-all text-sm ${
                        isLogin 
                          ? 'bg-transparent border-b-2 border-zinc-800 px-0 py-3 pr-10 focus:border-[#EAB308]' 
                          : 'bg-zinc-900/40 border border-zinc-800 px-3 py-3 pr-10 focus:border-[#EAB308] focus:bg-zinc-900/60'
                      }`}
                      placeholder="Enter password" required data-testid="signin-password" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className={`absolute top-1/2 -translate-y-1/2 text-zinc-700 hover:text-zinc-400 transition-colors ${isLogin ? 'right-0' : 'right-3'}`}>
                      {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>

                <Button type="submit" disabled={loading}
                  className="w-full bg-[#E11D48] hover:bg-[#BE123C] text-white py-4 h-auto text-sm font-bold uppercase tracking-widest mt-3 group transition-all relative overflow-hidden" data-testid="btn-signin-submit">
                  <span className="relative z-10 flex items-center justify-center">
                    {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <ArrowRight size={16} className="mr-2 group-hover:translate-x-1 transition-transform" />}
                    {isLogin ? "Authenticate" : "Start Free Trial"}
                  </span>
                </Button>
              </form>

              <div className="mt-6 text-center anim-fade-in" style={{ animationDelay: '0.4s' }}>
                <p className="text-sm text-zinc-600">
                  {isLogin ? "New to GPI.Forensic?" : "Already have an account?"}{" "}
                  <button onClick={() => setIsLogin(!isLogin)} className="text-[#EAB308] hover:text-[#FDE047] font-bold transition-colors" data-testid="btn-toggle-auth">
                    {isLogin ? "Create account" : "Sign in"}
                  </button>
                </p>
              </div>

              {/* Framework badges */}
              <div className="mt-10 pt-6 border-t border-zinc-900 anim-fade-in" style={{ animationDelay: '0.5s' }}>
                <p className="text-[9px] uppercase tracking-[0.3em] text-zinc-700 font-mono text-center mb-4">Supported Frameworks</p>
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  {["FIDIC", "NEC4", "JCT", "AIA", "ICC", "GCC", "AS4000"].map((fw, i) => (
                    <span key={i} className="text-[10px] font-bold font-mono text-zinc-600 px-2.5 py-1 border border-zinc-800 hover:border-zinc-600 hover:text-zinc-400 transition-all cursor-default">
                      {fw}
                    </span>
                  ))}
                </div>
              </div>

              {/* Trust indicators */}
              <div className="mt-6 flex items-center justify-center gap-4 text-[9px] font-mono text-zinc-700 uppercase tracking-wider anim-fade-in" style={{ animationDelay: '0.6s' }}>
                <div className="flex items-center gap-1">
                  <Shield size={10} />
                  <span>256-bit SSL</span>
                </div>
                <span className="text-zinc-800">|</span>
                <div className="flex items-center gap-1">
                  <Lock size={10} />
                  <span>2FA Auth</span>
                </div>
                <span className="text-zinc-800">|</span>
                <div className="flex items-center gap-1">
                  <Globe size={10} />
                  <span>GDPR</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignInPage;
